import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    Settings, 
    Save, 
    RotateCcw, 
    LayoutGrid, 
    Monitor, 
    ChefHat, 
    CheckCircle, 
    Clock, 
    TrendingUp,
    AlertTriangle,
    Info,
    Loader2,
    ArrowLeft
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useNavigate } from 'react-router-dom';

const SupervisionSettingsPage = () => {
    const { api, user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({
        // Tables
        tables_orange: 7,
        tables_red: 11,
        // Caisses
        caisses_expected: 1,
        // Cuisine
        kitchen_orange: 6,
        kitchen_red: 11,
        // Commandes pretes
        ready_orange: 3,
        ready_red: 6,
        // Temps de preparation
        prep_time_orange: 9,
        prep_time_red: 12,
        // Chiffre d'affaires
        ca_target_daily: 100000,
        minutes_no_sale_alert: 30,
        // Heures d'ouverture
        opening_hour: 10,
        closing_hour: 22
    });
    const [originalSettings, setOriginalSettings] = useState(null);

    // Check admin access
    const isAdmin = user?.role === 'ADMIN';
    const isManager = user?.role === 'ADMIN' || user?.role === 'DIRECTEUR';

    useEffect(() => {
        if (!isManager) {
            toast.error("Accès réservé aux managers");
            navigate('/pos-v2');
            return;
        }
        fetchSettings();
    }, [isManager, navigate]);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const res = await api().get('/pos/supervision/settings');
            setSettings(res.data);
            setOriginalSettings(res.data);
        } catch (error) {
            console.error('Error fetching settings:', error);
            toast.error('Erreur de chargement des paramètres');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => {
        const numValue = parseInt(value) || 0;
        setSettings(prev => ({ ...prev, [field]: numValue }));
    };

    const handleSave = async () => {
        if (!isAdmin) {
            toast.error("Seul l'administrateur peut modifier ces paramètres");
            return;
        }

        // Validation
        if (settings.tables_orange >= settings.tables_red) {
            toast.error("Le seuil orange des tables doit être inférieur au seuil rouge");
            return;
        }
        if (settings.kitchen_orange >= settings.kitchen_red) {
            toast.error("Le seuil orange de la cuisine doit être inférieur au seuil rouge");
            return;
        }
        if (settings.ready_orange >= settings.ready_red) {
            toast.error("Le seuil orange des commandes prêtes doit être inférieur au seuil rouge");
            return;
        }
        if (settings.prep_time_orange >= settings.prep_time_red) {
            toast.error("Le seuil orange du temps de préparation doit être inférieur au seuil rouge");
            return;
        }
        if (settings.opening_hour >= settings.closing_hour) {
            toast.error("L'heure d'ouverture doit être avant l'heure de fermeture");
            return;
        }

        try {
            setSaving(true);
            await api().put('/pos/supervision/settings', settings);
            setOriginalSettings(settings);
            toast.success('Paramètres sauvegardés avec succès');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        if (originalSettings) {
            setSettings(originalSettings);
            toast.info('Modifications annulées');
        }
    };

    const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);

    // Color preview component
    const ColorPreview = ({ value, orange, red }) => {
        let color = 'bg-green-500';
        let label = 'Vert';
        if (value >= red) {
            color = 'bg-red-500';
            label = 'Rouge';
        } else if (value >= orange) {
            color = 'bg-orange-500';
            label = 'Orange';
        }
        return (
            <div className="flex items-center gap-2 mt-1">
                <div className={cn("w-3 h-3 rounded-full", color)} />
                <span className="text-xs text-muted-foreground">
                    Actuellement: {label}
                </span>
            </div>
        );
    };

    // Threshold card component
    const ThresholdCard = ({ 
        icon: Icon, 
        title, 
        description, 
        color,
        orangeField, 
        redField, 
        orangeLabel = "Orange", 
        redLabel = "Rouge",
        unit = "",
        greenDescription,
        orangeDescription,
        redDescription
    }) => (
        <Card className="overflow-hidden">
            <div className={cn("h-1", color)} />
            <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                    <div className={cn("p-2 rounded-lg", color.replace("bg-", "bg-").replace("-500", "-100"))}>
                        <Icon className={cn("w-5 h-5", color.replace("bg-", "text-"))} />
                    </div>
                    <div>
                        <CardTitle className="text-base">{title}</CardTitle>
                        <CardDescription className="text-xs">{description}</CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Legend */}
                <div className="flex items-start gap-4 p-3 bg-muted/50 rounded-lg text-xs">
                    <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                        <span className="text-muted-foreground">{greenDescription || `< ${settings[orangeField]}${unit}`}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />
                        <span className="text-muted-foreground">{orangeDescription || `${settings[orangeField]}-${settings[redField]-1}${unit}`}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                        <span className="text-muted-foreground">{redDescription || `>= ${settings[redField]}${unit}`}</span>
                    </div>
                </div>

                {/* Input fields */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label className="text-xs text-orange-600 font-medium flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-orange-500" />
                            Seuil {orangeLabel}
                        </Label>
                        <div className="flex items-center gap-2 mt-1">
                            <Input
                                type="number"
                                min="0"
                                value={settings[orangeField]}
                                onChange={(e) => handleChange(orangeField, e.target.value)}
                                className="h-9"
                                disabled={!isAdmin}
                            />
                            {unit && <span className="text-sm text-muted-foreground">{unit}</span>}
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs text-red-600 font-medium flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-red-500" />
                            Seuil {redLabel}
                        </Label>
                        <div className="flex items-center gap-2 mt-1">
                            <Input
                                type="number"
                                min="0"
                                value={settings[redField]}
                                onChange={(e) => handleChange(redField, e.target.value)}
                                className="h-9"
                                disabled={!isAdmin}
                            />
                            {unit && <span className="text-sm text-muted-foreground">{unit}</span>}
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-[60vh]">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                    <p className="text-muted-foreground">Chargement des paramètres...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto py-6 px-4 max-w-6xl" data-testid="supervision-settings-page">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                    <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => navigate('/pos-v2')}
                        className="h-9 w-9"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Settings className="w-7 h-7 text-primary" />
                            Paramètres Supervision Caisse
                        </h1>
                        <p className="text-muted-foreground text-sm mt-1">
                            Configurez les seuils des indicateurs de couleur pour la barre de supervision
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {hasChanges && (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                            Modifications non sauvegardées
                        </Badge>
                    )}
                    <Button 
                        variant="outline" 
                        onClick={handleReset}
                        disabled={!hasChanges || saving}
                    >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Annuler
                    </Button>
                    <Button 
                        onClick={handleSave}
                        disabled={!hasChanges || saving || !isAdmin}
                        className="min-w-[120px]"
                    >
                        {saving ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <Save className="w-4 h-4 mr-2" />
                        )}
                        Sauvegarder
                    </Button>
                </div>
            </div>

            {/* Admin notice */}
            {!isAdmin && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                        <p className="font-medium text-amber-800">Mode lecture seule</p>
                        <p className="text-sm text-amber-700">
                            Seul l'administrateur peut modifier ces paramètres. Vous pouvez consulter les valeurs actuelles.
                        </p>
                    </div>
                </div>
            )}

            {/* Info box */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                    <p className="font-medium text-blue-800">Comment fonctionnent les couleurs ?</p>
                    <p className="text-sm text-blue-700 mt-1">
                        Les indicateurs de la barre de supervision changent de couleur en fonction des seuils définis ci-dessous. 
                        <span className="inline-flex items-center gap-1 mx-1">
                            <span className="w-2 h-2 rounded-full bg-green-500" />
                            <strong>Vert</strong>
                        </span> 
                        = situation normale, 
                        <span className="inline-flex items-center gap-1 mx-1">
                            <span className="w-2 h-2 rounded-full bg-orange-500" />
                            <strong>Orange</strong>
                        </span> 
                        = attention requise, 
                        <span className="inline-flex items-center gap-1 mx-1">
                            <span className="w-2 h-2 rounded-full bg-red-500" />
                            <strong>Rouge</strong>
                        </span> 
                        = situation critique.
                    </p>
                </div>
            </div>

            {/* Threshold Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
                {/* Tables */}
                <ThresholdCard
                    icon={LayoutGrid}
                    title="Tables Ouvertes"
                    description="Nombre de tables avec commandes en cours"
                    color="bg-blue-500"
                    orangeField="tables_orange"
                    redField="tables_red"
                    greenDescription={`0-${settings.tables_orange - 1} tables`}
                    orangeDescription={`${settings.tables_orange}-${settings.tables_red - 1} tables`}
                    redDescription={`${settings.tables_red}+ tables`}
                />

                {/* Caisses */}
                <Card className="overflow-hidden">
                    <div className="h-1 bg-purple-500" />
                    <CardHeader className="pb-2">
                        <div className="flex items-center gap-2">
                            <div className="p-2 rounded-lg bg-purple-100">
                                <Monitor className="w-5 h-5 text-purple-500" />
                            </div>
                            <div>
                                <CardTitle className="text-base">Caisses Actives</CardTitle>
                                <CardDescription className="text-xs">Sessions de caisse ouvertes</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex items-start gap-4 p-3 bg-muted/50 rounded-lg text-xs">
                            <div className="flex items-center gap-1.5">
                                <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                                <span className="text-muted-foreground">&gt; {settings.caisses_expected} caisse(s)</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />
                                <span className="text-muted-foreground">= {settings.caisses_expected} caisse</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                                <span className="text-muted-foreground">0 ou erreur</span>
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground font-medium">
                                Nombre de caisses attendues
                            </Label>
                            <Input
                                type="number"
                                min="1"
                                max="10"
                                value={settings.caisses_expected}
                                onChange={(e) => handleChange('caisses_expected', e.target.value)}
                                className="h-9 mt-1 w-24"
                                disabled={!isAdmin}
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Kitchen Orders */}
                <ThresholdCard
                    icon={ChefHat}
                    title="Commandes en Cuisine"
                    description="Commandes envoyées, non encore prêtes"
                    color="bg-orange-500"
                    orangeField="kitchen_orange"
                    redField="kitchen_red"
                    greenDescription={`0-${settings.kitchen_orange - 1} commandes`}
                    orangeDescription={`${settings.kitchen_orange}-${settings.kitchen_red - 1} cmd`}
                    redDescription={`${settings.kitchen_red}+ commandes`}
                />

                {/* Ready Orders */}
                <ThresholdCard
                    icon={CheckCircle}
                    title="Commandes Prêtes"
                    description="Commandes prêtes en attente de service"
                    color="bg-green-500"
                    orangeField="ready_orange"
                    redField="ready_red"
                    greenDescription={`0-${settings.ready_orange - 1} en attente`}
                    orangeDescription={`${settings.ready_orange}-${settings.ready_red - 1} en attente`}
                    redDescription={`${settings.ready_red}+ en attente`}
                />

                {/* Prep Time */}
                <ThresholdCard
                    icon={Clock}
                    title="Temps Moyen Cuisine"
                    description="Temps de préparation moyen des commandes"
                    color="bg-amber-500"
                    orangeField="prep_time_orange"
                    redField="prep_time_red"
                    unit="min"
                    greenDescription={`<= ${settings.prep_time_orange} min`}
                    orangeDescription={`${settings.prep_time_orange + 1}-${settings.prep_time_red} min`}
                    redDescription={`> ${settings.prep_time_red} min`}
                />

                {/* Revenue */}
                <Card className="overflow-hidden">
                    <div className="h-1 bg-emerald-500" />
                    <CardHeader className="pb-2">
                        <div className="flex items-center gap-2">
                            <div className="p-2 rounded-lg bg-emerald-100">
                                <TrendingUp className="w-5 h-5 text-emerald-500" />
                            </div>
                            <div>
                                <CardTitle className="text-base">Chiffre d'Affaires</CardTitle>
                                <CardDescription className="text-xs">Objectif journalier et alertes</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex items-start gap-4 p-3 bg-muted/50 rounded-lg text-xs">
                            <div className="flex items-center gap-1.5">
                                <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                                <span className="text-muted-foreground">&gt;= 80% objectif</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />
                                <span className="text-muted-foreground">50-80% objectif</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                                <span className="text-muted-foreground">&lt; 50% ou inactivité</span>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs text-muted-foreground font-medium">
                                    Objectif CA journalier
                                </Label>
                                <div className="flex items-center gap-2 mt-1">
                                    <Input
                                        type="number"
                                        min="0"
                                        step="10000"
                                        value={settings.ca_target_daily}
                                        onChange={(e) => handleChange('ca_target_daily', e.target.value)}
                                        className="h-9"
                                        disabled={!isAdmin}
                                    />
                                    <span className="text-sm text-muted-foreground">F</span>
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs text-muted-foreground font-medium">
                                    Alerte sans vente
                                </Label>
                                <div className="flex items-center gap-2 mt-1">
                                    <Input
                                        type="number"
                                        min="5"
                                        max="120"
                                        value={settings.minutes_no_sale_alert}
                                        onChange={(e) => handleChange('minutes_no_sale_alert', e.target.value)}
                                        className="h-9"
                                        disabled={!isAdmin}
                                    />
                                    <span className="text-sm text-muted-foreground">min</span>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Operating Hours */}
            <Card className="mb-6">
                <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                        <Clock className="w-5 h-5 text-primary" />
                        <CardTitle className="text-base">Heures d'Ouverture</CardTitle>
                    </div>
                    <CardDescription>
                        Définissez les heures d'ouverture du restaurant pour le calcul de l'objectif CA
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 gap-6 max-w-md">
                        <div>
                            <Label className="text-sm font-medium">Heure d'ouverture</Label>
                            <div className="flex items-center gap-2 mt-1">
                                <Input
                                    type="number"
                                    min="0"
                                    max="23"
                                    value={settings.opening_hour}
                                    onChange={(e) => handleChange('opening_hour', e.target.value)}
                                    className="h-10 w-20"
                                    disabled={!isAdmin}
                                />
                                <span className="text-muted-foreground">h00</span>
                            </div>
                        </div>
                        <div>
                            <Label className="text-sm font-medium">Heure de fermeture</Label>
                            <div className="flex items-center gap-2 mt-1">
                                <Input
                                    type="number"
                                    min="0"
                                    max="23"
                                    value={settings.closing_hour}
                                    onChange={(e) => handleChange('closing_hour', e.target.value)}
                                    className="h-10 w-20"
                                    disabled={!isAdmin}
                                />
                                <span className="text-muted-foreground">h00</span>
                            </div>
                        </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3">
                        Ces heures sont utilisées pour calculer le pourcentage d'objectif CA attendu à chaque heure de la journée.
                    </p>
                </CardContent>
            </Card>

            {/* Preview Section */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <Info className="w-4 h-4" />
                        Aperçu des Seuils
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b">
                                    <th className="text-left py-2 px-3 font-medium">Indicateur</th>
                                    <th className="text-center py-2 px-3 font-medium">
                                        <span className="inline-flex items-center gap-1">
                                            <span className="w-2 h-2 rounded-full bg-green-500" />
                                            Vert
                                        </span>
                                    </th>
                                    <th className="text-center py-2 px-3 font-medium">
                                        <span className="inline-flex items-center gap-1">
                                            <span className="w-2 h-2 rounded-full bg-orange-500" />
                                            Orange
                                        </span>
                                    </th>
                                    <th className="text-center py-2 px-3 font-medium">
                                        <span className="inline-flex items-center gap-1">
                                            <span className="w-2 h-2 rounded-full bg-red-500" />
                                            Rouge
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="border-b">
                                    <td className="py-2 px-3">Tables ouvertes</td>
                                    <td className="text-center py-2 px-3">0 - {settings.tables_orange - 1}</td>
                                    <td className="text-center py-2 px-3">{settings.tables_orange} - {settings.tables_red - 1}</td>
                                    <td className="text-center py-2 px-3">{settings.tables_red}+</td>
                                </tr>
                                <tr className="border-b">
                                    <td className="py-2 px-3">Caisses actives</td>
                                    <td className="text-center py-2 px-3">&gt; {settings.caisses_expected}</td>
                                    <td className="text-center py-2 px-3">= {settings.caisses_expected}</td>
                                    <td className="text-center py-2 px-3">0 ou erreur</td>
                                </tr>
                                <tr className="border-b">
                                    <td className="py-2 px-3">Commandes en cuisine</td>
                                    <td className="text-center py-2 px-3">0 - {settings.kitchen_orange - 1}</td>
                                    <td className="text-center py-2 px-3">{settings.kitchen_orange} - {settings.kitchen_red - 1}</td>
                                    <td className="text-center py-2 px-3">{settings.kitchen_red}+</td>
                                </tr>
                                <tr className="border-b">
                                    <td className="py-2 px-3">Commandes prêtes</td>
                                    <td className="text-center py-2 px-3">0 - {settings.ready_orange - 1}</td>
                                    <td className="text-center py-2 px-3">{settings.ready_orange} - {settings.ready_red - 1}</td>
                                    <td className="text-center py-2 px-3">{settings.ready_red}+</td>
                                </tr>
                                <tr className="border-b">
                                    <td className="py-2 px-3">Temps moyen cuisine</td>
                                    <td className="text-center py-2 px-3">&lt;= {settings.prep_time_orange} min</td>
                                    <td className="text-center py-2 px-3">{settings.prep_time_orange + 1} - {settings.prep_time_red} min</td>
                                    <td className="text-center py-2 px-3">&gt; {settings.prep_time_red} min</td>
                                </tr>
                                <tr>
                                    <td className="py-2 px-3">Chiffre d'affaires</td>
                                    <td className="text-center py-2 px-3">&gt;= 80% objectif</td>
                                    <td className="text-center py-2 px-3">50 - 79%</td>
                                    <td className="text-center py-2 px-3">&lt; 50% ou inactif</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default SupervisionSettingsPage;
